// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import dynamicProto from "@microsoft/dynamicproto-js";
import { isNullOrUndefined, isObject } from "@microsoft/applicationinsights-core-js";
import { createPromise, doAwaitResponse } from "@nevware21/ts-async";
import { IndexedDbProvider } from "./Providers/IndexDbProvider";
import { WebStorageProvider } from "./Providers/WebStorageProvider";
import { _DYN_ADD_EVENT, _DYN_ATTEMP_CNT, _DYN_CLEAR, _DYN_DATA, _DYN_DIAG_LOG, _DYN_GET_NEXT_BATCH, _DYN_INDEX_OF, _DYN_INITIALIZE, _DYN_IS_ARR, _DYN_ITEM_CTX, _DYN_LENGTH, _DYN_REJECTED, _DYN_REMOVE_EVENTS, _DYN_SEND_POST, _DYN_STATE, _DYN_STORAGE_CONFIG, _DYN_TEARDOWN, _DYN_VALUE } from "./__DynamicConstants";
var MaxStorageProviderConfig = 2;
var NoProviderErrMsg = "No provider is available";
var OfflineBatchHandler = /** @class */ (function () {
    function OfflineBatchHandler(logger, unloadHookContainer) {
        dynamicProto(OfflineBatchHandler, this, function (_self) {
            var _isInitialized;
            var _logger;
            var _provider;
            var _unloadProvider;
            var _itemCtx;
            var _maxRetryCnt;
            var _retryCodes;
            _initDefaults();
            _self[_DYN_INITIALIZE /* @min:%2einitialize */] = function (providerContext) {
                // TODO: handle provider change
                try {
                    if (!_isInitialized) {
                        _logger = logger || providerContext.itemCtx[_DYN_DIAG_LOG /* @min:%2ediagLog */]();
                        _itemCtx = providerContext[_DYN_ITEM_CTX /* @min:%2eitemCtx */];
                        _provider = _initProvider(providerContext);
                        var storeCfg = providerContext[_DYN_STORAGE_CONFIG /* @min:%2estorageConfig */] || {};
                        var retry = storeCfg.maxRetry;
                        _maxRetryCnt = !isNullOrUndefined(retry) ? retry : 2;
                        _retryCodes = (storeCfg.senderCfg || {}).retryCodes;
                        if (_provider) {
                            _isInitialized = true;
                        }
                    }
                }
                catch (e) {
                    _isInitialized = false;
                }
                return _isInitialized;
            };
            _self["_getDbgPlgTargets"] = function () {
                return [_provider, _isInitialized, _unloadProvider];
            };
            _self.storeBatch = function (batch, cb, sync) {
                var provider = _provider;
                if (!!sync) {
                    provider = _unloadProvider;
                }
                return createPromise(function (resolve, reject) {
                    if (!provider) {
                        reject(new Error(NoProviderErrMsg));
                    }
                    var evt = _getOfflineEvt(batch);
                    return doAwaitResponse(provider[_DYN_ADD_EVENT /* @min:%2eaddEvent */](evt.id, evt, _itemCtx), function (response) {
                        try {
                            var evt_1 = response[_DYN_VALUE /* @min:%2evalue */] || response.reason || [];
                            var state = 2 /* eBatchStoreStatus.Failure */;
                            if (!response[_DYN_REJECTED /* @min:%2erejected */]) {
                                state = 1 /* eBatchStoreStatus.Success */;
                            }
                            var res = { state: state, item: evt_1 };
                            try {
                                cb && cb(res);
                            }
                            catch (e) {
                                // eslint-disable-next-line no-empty
                            }
                            resolve(res);
                        }
                        catch (e) {
                            reject(e);
                        }
                    });
                });
            };
            _self.sendNextBatch = function (cb, sync, xhrOverride, cnt) {
                if (cnt === void 0) { cnt = 1; }
                return createPromise(function (resolve, reject) {
                    if (!_provider && !_unloadProvider) {
                        reject(new Error(NoProviderErrMsg));
                    }
                    function storeResolve(result) {
                        try {
                            cb && cb(result);
                        }
                        catch (e) {
                            // eslint-disable-next-line no-empty
                        }
                        resolve(result);
                    }
                    var provider = _provider;
                    try {
                        if (_unloadProvider) {
                            doAwaitResponse(_unloadProvider[_DYN_GET_NEXT_BATCH /* @min:%2egetNextBatch */](), function (res) {
                                if (res && !res[_DYN_REJECTED /* @min:%2erejected */]) {
                                    var unloadEvts = res[_DYN_VALUE /* @min:%2evalue */];
                                    if (unloadEvts && unloadEvts[_DYN_LENGTH /* @min:%2elength */]) {
                                        // always try to send ones from local storage
                                        provider = _unloadProvider;
                                        return _handleNextBatch(unloadEvts);
                                    }
                                }
                                return _sendNextBatch();
                            });
                        }
                        else {
                            return _sendNextBatch();
                        }
                    }
                    catch (e) {
                        // eslint-disable-next-line no-empty
                        reject(e);
                    }
                    function _sendNextBatch() {
                        return doAwaitResponse(provider[_DYN_GET_NEXT_BATCH /* @min:%2egetNextBatch */](), function (response) {
                            try {
                                if (!response || response[_DYN_REJECTED /* @min:%2erejected */]) {
                                    var res = { state: 4 /* eBatchSendStatus.Failure */, data: response.reason };
                                    storeResolve(res);
                                    return;
                                }
                                var evts = response[_DYN_VALUE /* @min:%2evalue */] || [];
                                return _handleNextBatch(evts);
                            }
                            catch (e) {
                                reject(e);
                            }
                        });
                    }
                    function _handleNextBatch(evts) {
                        var result = { state: 1 /* eBatchSendStatus.Complete */, data: null };
                        if (evts[_DYN_LENGTH /* @min:%2elength */]) {
                            // make sure doawait has resolve
                            return doAwaitResponse(provider[_DYN_REMOVE_EVENTS /* @min:%2eremoveEvents */](evts), function (res) {
                                if (res && !res.rejected && res[_DYN_VALUE /* @min:%2evalue */]) {
                                    var deletedItems = res[_DYN_VALUE /* @min:%2evalue */];
                                    var _loop_1 = function (lp) {
                                        try {
                                            var evt_2 = evts[lp];
                                            result[_DYN_DATA /* @min:%2edata */] = evt_2; // add data
                                            if (xhrOverride && xhrOverride[_DYN_SEND_POST /* @min:%2esendPOST */]) {
                                                evt_2[_DYN_ATTEMP_CNT /* @min:%2eattempCnt */]++;
                                                var sender = xhrOverride[_DYN_SEND_POST /* @min:%2esendPOST */]; // use transports
                                                var onCompleteCallback = function (status, headers, res) {
                                                    if (status == 200) { // status code (e.g: invalid key) drop payload
                                                        result[_DYN_STATE /* @min:%2estate */] = 1 /* eBatchSendStatus.Complete */;
                                                    }
                                                    else {
                                                        var isRetriable = _isRetriable(evt_2, status);
                                                        if (isRetriable) {
                                                            result[_DYN_STATE /* @min:%2estate */] = 2 /* eBatchSendStatus.Retry */;
                                                            _unloadProvider && _unloadProvider[_DYN_ADD_EVENT /* @min:%2eaddEvent */](evt_2.id, evt_2, _itemCtx);
                                                        }
                                                        else {
                                                            result[_DYN_STATE /* @min:%2estate */] = 3 /* eBatchSendStatus.Drop */;
                                                        }
                                                    }
                                                    storeResolve(result);
                                                };
                                                return { value: doAwaitResponse(sender(evt_2, onCompleteCallback, sync), function (res) {
                                                        // do nothing, because it should be resolved in callback
                                                    }) };
                                            }
                                            // call resolve
                                            result[_DYN_STATE /* @min:%2estate */] = 3 /* eBatchSendStatus.Drop */;
                                            storeResolve(result);
                                            return { value: void 0 };
                                        }
                                        catch (e) {
                                            reject(e);
                                        }
                                    };
                                    for (var lp = 0; lp < deletedItems[_DYN_LENGTH /* @min:%2elength */]; lp++) {
                                        var state_1 = _loop_1(lp);
                                        if (typeof state_1 === "object")
                                            return state_1.value;
                                    }
                                    // this case should never happen because this function is called after we confirm that evt is available
                                    // add resolve here in case
                                    result[_DYN_STATE /* @min:%2estate */] = 1 /* eBatchSendStatus.Complete */;
                                    storeResolve(result);
                                    return;
                                }
                                // if can not delete first, resolve
                                result[_DYN_STATE /* @min:%2estate */] = 4 /* eBatchSendStatus.Failure */;
                                result[_DYN_DATA /* @min:%2edata */] = res.reason;
                                storeResolve(result);
                                return;
                            });
                        }
                        storeResolve(result);
                    }
                });
            };
            _self.hasStoredBatch = function (cb) {
                return createPromise(function (resolve, reject) {
                    if (!_provider) {
                        reject(new Error(NoProviderErrMsg));
                    }
                    return doAwaitResponse(_provider[_DYN_GET_NEXT_BATCH /* @min:%2egetNextBatch */](), function (response) {
                        try {
                            var evts = response[_DYN_VALUE /* @min:%2evalue */] || [];
                            var hasEvts = evts[_DYN_LENGTH /* @min:%2elength */] > 0;
                            cb && cb(hasEvts);
                            resolve(hasEvts);
                        }
                        catch (e) {
                            reject(e);
                        }
                    });
                });
            };
            _self.cleanStorage = function (cb) {
                return createPromise(function (resolve, reject) {
                    // note: doawaitresponse currently returns undefined
                    if (!_provider) {
                        reject(new Error(NoProviderErrMsg));
                    }
                    return doAwaitResponse(_provider[_DYN_CLEAR /* @min:%2eclear */](), function (response) {
                        try {
                            var cnt = 0;
                            if (!response[_DYN_REJECTED /* @min:%2erejected */]) {
                                var evts = response[_DYN_VALUE /* @min:%2evalue */] || [];
                                cnt = evts[_DYN_LENGTH /* @min:%2elength */];
                            }
                            var res = { batchCnt: cnt };
                            cb && cb(res); // get value. make sure to use callback
                            resolve(res); // won't get val
                        }
                        catch (e) {
                            reject(e);
                        }
                    });
                });
            };
            _self[_DYN_TEARDOWN /* @min:%2eteardown */] = function (unloadCtx, unloadState) {
                _provider && _provider[_DYN_TEARDOWN /* @min:%2eteardown */]();
                _initDefaults();
            };
            function _initDefaults() {
                _provider = null;
                _unloadProvider = null;
                _isInitialized = false;
                _maxRetryCnt = null;
                _retryCodes = null;
            }
            function _tryGetIndexedDbProvider(providerContext) {
                var newProvider = new IndexedDbProvider(undefined, unloadHookContainer);
                if (!newProvider[_DYN_INITIALIZE /* @min:%2einitialize */](providerContext)) {
                    // Failed to initialize the provider
                    newProvider = null;
                }
                return newProvider;
            }
            function _tryGetWebStorageProvider(storageType, providerContext) {
                var newProvider = new WebStorageProvider(storageType, undefined, unloadHookContainer);
                if (!newProvider[_DYN_INITIALIZE /* @min:%2einitialize */](providerContext)) {
                    // Failed to initialize the provider
                    newProvider = null;
                }
                return newProvider;
            }
            function _initProvider(providerContext) {
                var providers = providerContext[_DYN_STORAGE_CONFIG /* @min:%2estorageConfig */].providers;
                var newProvider = null;
                var lp = 0;
                while (!newProvider && lp < providers[_DYN_LENGTH /* @min:%2elength */] && lp < MaxStorageProviderConfig) {
                    switch (providers[lp++]) {
                        case 1 /* eStorageProviders.LocalStorage */:
                            newProvider = _tryGetWebStorageProvider("localStorage", providerContext);
                            _unloadProvider = newProvider;
                            break;
                        case 2 /* eStorageProviders.SessionStorage */:
                            newProvider = _tryGetWebStorageProvider("sessionStorage", providerContext);
                            _unloadProvider = newProvider;
                            break;
                        case 3 /* eStorageProviders.IndexedDb */:
                            newProvider = _tryGetIndexedDbProvider(providerContext);
                            _unloadProvider = _tryGetWebStorageProvider("localStorage", providerContext);
                    }
                }
                return newProvider;
            }
            function _getOfflineEvt(batch) {
                var item = batch;
                // TODO: add details
                var data = item[_DYN_DATA /* @min:%2edata */];
                if (data) {
                    var isArr = isObject(data);
                    item[_DYN_IS_ARR /* @min:%2eisArr */] = isArr;
                }
                return item;
            }
            /**
             * Checks if the SDK should resend the payload after receiving this status code from the backend.
             * @param statusCode
            */
            function _isRetriable(item, statusCode) {
                if (!_shouldSend(item)) {
                    return false;
                }
                // retryCodes = [] means should not retry
                if (!isNullOrUndefined(_retryCodes)) {
                    return _retryCodes[_DYN_LENGTH /* @min:%2elength */] && _retryCodes[_DYN_INDEX_OF /* @min:%2eindexOf */](statusCode) > -1;
                }
                return statusCode === 401 // Unauthorized
                    || statusCode === 403 // Forbidden
                    || statusCode === 408 // Timeout
                    || statusCode === 429 // Too many requests.
                    || statusCode === 500 // Internal server error.
                    || statusCode === 502 // Bad Gateway.
                    || statusCode === 503 // Service unavailable.
                    || statusCode === 504; // Gateway timeout.
            }
            function _shouldSend(item) {
                if (item && item[_DYN_DATA /* @min:%2edata */]) {
                    item[_DYN_ATTEMP_CNT /* @min:%2eattempCnt */] = item[_DYN_ATTEMP_CNT /* @min:%2eattempCnt */] || 0;
                    return item[_DYN_ATTEMP_CNT /* @min:%2eattempCnt */] <= _maxRetryCnt;
                }
                return false;
            }
        });
    }
    /**
     * Initializes the provider using the config
     * @returns True if the provider is initialized and available for use otherwise false
     */
    OfflineBatchHandler.prototype.initialize = function (providerContext) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    OfflineBatchHandler.prototype.storeBatch = function (batch, cb, sync) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    OfflineBatchHandler.prototype.sendNextBatch = function (cb, sync, xhrOverride, cnt) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    OfflineBatchHandler.prototype.hasStoredBatch = function (callback) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    OfflineBatchHandler.prototype.cleanStorage = function (cb) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    OfflineBatchHandler.prototype.teardown = function (unloadCtx, unloadState) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    return OfflineBatchHandler;
}());
export { OfflineBatchHandler };
//# sourceMappingURL=OfflineBatchHandler.js.map